import type { SmartGuideBrowserLocation } from "../BrowserLocation/SmartGuideBrowserLocation";
import type { GpsCoordinate } from "./GpsTriggerGeometry";
import { distanceToPolygonMeters, pointInPolygon } from "./GpsTriggerGeometry";
import type { GpsTriggerContext, GpsTriggerItem, GpsTriggerPolygon } from "./GpsTriggerContextParser";
import { GpsTriggerSpatialIndex } from "./GpsTriggerSpatialIndex";
import { GpsTriggerVisitedStore } from "./GpsTriggerVisitedStore";

const maximumFixAgeMs = 30_000;
const maximumAccuracyMeters = 100;
const minimumProximitySearchRadiusMeters = 1_100;
const metersPerLatitudeDegree = 111_000;

type IndexedZone = {
    polygon: GpsTriggerPolygon;
    south: number;
    west: number;
    north: number;
    east: number;
};

type IndexedItem = {
    item: GpsTriggerItem;
    zones: IndexedZone[];
};

export class GpsTriggerEngine {
    private readonly indexedItems: IndexedItem[];
    private readonly spatialIndex: GpsTriggerSpatialIndex;
    private enteredItemIds = new Set<string>();
    private readonly mode: GpsTriggerContext["mode"];

    constructor(
        context: GpsTriggerContext,
        private readonly visitedStore: GpsTriggerVisitedStore,
        private readonly now: () => number = Date.now
    ) {
        this.mode = context.mode;
        visitedStore.refresh();
        if (this.mode === "tour") {
            for (const item of context.items) {
                if (item.basePoiId && visitedStore.has(item.id) && !visitedStore.has(item.basePoiId)) {
                    visitedStore.mark(item.id, item.basePoiId);
                }
            }
        }
        this.indexedItems = context.items.map(item => ({
            item,
            zones: item.zones.map(polygon => ({ polygon, ...bounds(polygon.vertices) }))
        }));
        this.spatialIndex = new GpsTriggerSpatialIndex(this.indexedItems.flatMap((candidate, sourceIndex) =>
            candidate.zones.map(zone => ({ sourceIndex, ...zone }))
        ));
    }

    get remainingCount(): number {
        this.visitedStore.refresh();
        return this.indexedItems.filter(candidate => !this.visitedStore.has(candidate.item.id)).length;
    }

    clearEnteredZones(): void {
        this.enteredItemIds.clear();
    }

    evaluate(location: SmartGuideBrowserLocation): GpsTriggerItem | undefined {
        this.visitedStore.refresh();
        if (!this.isAcceptableFix(location)) {
            return undefined;
        }

        const point = { latitude: location.latitude, longitude: location.longitude };
        const currentlyEntered = new Set<string>();
        for (const sourceIndex of this.spatialIndex.queryPoint(point.latitude, point.longitude)) {
            const candidate = this.indexedItems[sourceIndex];
            if (this.visitedStore.has(candidate.item.id)) {
                continue;
            }
            if (candidate.zones.some(zone => contains(zone, point))) {
                currentlyEntered.add(candidate.item.id);
            }
        }

        const triggered = this.indexedItems.find(candidate =>
            currentlyEntered.has(candidate.item.id) &&
            !this.enteredItemIds.has(candidate.item.id) &&
            !this.visitedStore.has(candidate.item.id)
        )?.item;
        this.enteredItemIds = currentlyEntered;
        if (triggered) {
            this.visitedStore.mark(triggered.id, this.mode === "tour" ? triggered.basePoiId : null);
        }

        return triggered;
    }

    distanceToNearestUnvisitedZone(
        location: Pick<SmartGuideBrowserLocation, "latitude" | "longitude">,
        searchRadiusMeters = minimumProximitySearchRadiusMeters
    ): number {
        this.visitedStore.refresh();
        if (!Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
            return Number.POSITIVE_INFINITY;
        }

        const point = { latitude: location.latitude, longitude: location.longitude };
        const effectiveSearchRadius = Math.max(searchRadiusMeters, minimumProximitySearchRadiusMeters);
        const latitudeDelta = effectiveSearchRadius / metersPerLatitudeDegree;
        const longitudeScale = Math.max(Math.abs(Math.cos(location.latitude * Math.PI / 180)), 0.01);
        const longitudeDelta = effectiveSearchRadius / (metersPerLatitudeDegree * longitudeScale);
        const nearbySourceIndexes = this.spatialIndex.queryEnvelope(
            location.latitude - latitudeDelta,
            location.longitude - longitudeDelta,
            location.latitude + latitudeDelta,
            location.longitude + longitudeDelta
        );
        let shortest = Number.POSITIVE_INFINITY;
        for (const sourceIndex of nearbySourceIndexes) {
            const candidate = this.indexedItems[sourceIndex];
            if (this.visitedStore.has(candidate.item.id)) {
                continue;
            }
            for (const zone of candidate.zones) {
                shortest = Math.min(shortest, distanceToPolygonMeters(point, zone.polygon.vertices));
            }
        }
        return shortest;
    }

    distanceToNearestUnvisitedZoneAnywhere(
        location: Pick<SmartGuideBrowserLocation, "latitude" | "longitude">
    ): number {
        this.visitedStore.refresh();
        if (!Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
            return Number.POSITIVE_INFINITY;
        }

        const point = { latitude: location.latitude, longitude: location.longitude };
        let shortest = Number.POSITIVE_INFINITY;
        for (const candidate of this.indexedItems) {
            if (this.visitedStore.has(candidate.item.id)) {
                continue;
            }
            for (const zone of candidate.zones) {
                shortest = Math.min(shortest, distanceToPolygonMeters(point, zone.polygon.vertices));
            }
        }
        return shortest;
    }

    private isAcceptableFix(location: SmartGuideBrowserLocation): boolean {
        return location.source === "gps" &&
            Number.isFinite(location.latitude) &&
            Number.isFinite(location.longitude) &&
            Number.isFinite(location.timestamp) &&
            this.now() - location.timestamp <= maximumFixAgeMs &&
            typeof location.accuracy === "number" &&
            Number.isFinite(location.accuracy) &&
            location.accuracy >= 0 &&
            location.accuracy <= maximumAccuracyMeters;
    }
}

function bounds(vertices: readonly GpsCoordinate[]): Omit<IndexedZone, "polygon"> {
    return {
        south: Math.min(...vertices.map(vertex => vertex.latitude)),
        west: Math.min(...vertices.map(vertex => vertex.longitude)),
        north: Math.max(...vertices.map(vertex => vertex.latitude)),
        east: Math.max(...vertices.map(vertex => vertex.longitude))
    };
}

function contains(zone: IndexedZone, point: GpsCoordinate): boolean {
    return point.latitude >= zone.south &&
        point.latitude <= zone.north &&
        point.longitude >= zone.west &&
        point.longitude <= zone.east &&
        pointInPolygon(point, zone.polygon.vertices);
}
